import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sliders, Truck, Percent, Tag } from 'react-feather';

import '../../ControlPanel/sadmin.css';
import '../Settings/settings.css';
import './configuration.css';

import DeliveryPartnerAdd from './DeliveryPartner/DeliveryPartnerAdd';
import RevenueShareMethod from './RevenueShareMethod';
import OtherCharges from './OtherCharges';

/**
 * /admin/configurations — concessionaire-scoped configuration hub.
 *
 * Assign delivery partners, revenue-share methods and other charges to the
 * logged-in concessionaire. Three tabs, each a dual-pane transfer list
 * (see shared/TransferList.jsx). V3 sadmin glass shell, matching SettingsHub.
 */

const TABS = [
    { id: 'delivery', label: 'Delivery Partners', icon: <Truck size={14} />, Component: DeliveryPartnerAdd },
    { id: 'revenue', label: 'Revenue Share Methods', icon: <Percent size={14} />, Component: RevenueShareMethod },
    { id: 'charges', label: 'Charges', icon: <Tag size={14} />, Component: OtherCharges },
];

const Configurations = () => {
    const { t } = useTranslation();
    const canManage = localStorage.getItem('manageConfigurations') === '1';
    const [tab, setTab] = useState('delivery');

    // Authorize in an effect so the redirect doesn't fire during render
    // (the old page called alert()/history.back() mid-render).
    useEffect(() => {
        if (!canManage) {
            window.alert(t('You are not authorized to view this page'));
            window.history.back();
        }
    }, [canManage, t]);

    const active = useMemo(() => TABS.find((t) => t.id === tab) || TABS[0], [tab]);
    const ActiveComponent = active.Component;

    if (!canManage) return null;

    return (
        <Fragment>
            <div className="v3-sadmin">
                {/* Hero ─────────────────────────────────────────────── */}
                <div className="v3-sadmin-hero">
                    <div className="v3-sadmin-hero-text">
                        <p className="v3-label" style={{ margin: 0 }}>{t('Concessionaire setup')}</p>
                        <h1 className="v3-display v3-sadmin-greeting">
                            <span className="v3-sadmin-name">{t('Configurations')}</span>
                        </h1>
                        <p className="v3-body v3-sadmin-sub">
                            {t('Choose which delivery partners, revenue-share methods and charges apply to this concessionaire. Changes save as you move items.')}
                        </p>
                    </div>
                    <div className="v3-sadmin-hero-actions">
                        <span className="v3-sadmin-kpi-icon accent" aria-hidden>
                            <Sliders size={18} />
                        </span>
                    </div>
                </div>

                {/* Hub card ─────────────────────────────────────────── */}
                <div className="v3-card v3-sadmin-list-card">
                    <div className="v3-settings-toolbar">
                        <div className="v3-settings-tabs" role="tablist">
                            {TABS.map((tb) => (
                                <button
                                    key={tb.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={tb.id === tab}
                                    className={`v3-settings-tab ${tb.id === tab ? 'active' : ''}`}
                                    onClick={() => setTab(tb.id)}
                                >
                                    {tb.icon}
                                    {t(tb.label)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <ActiveComponent canManage={canManage} />
                </div>
            </div>
        </Fragment>
    );
};

export default Configurations;
